import { useState } from 'react';
import { LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [tempUser, setTempUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: enter email, 2: enter code & new password
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!res.ok) {
        toast.error('Invalid credentials');
        setLoading(false);
        return;
      }

      const data = await res.json();
      
      // Check if password reset is required
      if (data.password_reset_required) {
        setTempToken(data.token);
        setTempUser(data.user);
        setShowPasswordChangeDialog(true);
        setLoading(false);
        return;
      }
      
      onLogin(data.token, data.user);
      toast.success('Login successful!');
    } catch (error) {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tempToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ new_password: newPassword })
      });

      if (!res.ok) {
        toast.error('Failed to change password');
        return;
      }

      toast.success('Password changed successfully!');
      setShowPasswordChangeDialog(false);
      onLogin(tempToken, tempUser);
    } catch (error) {
      toast.error('Failed to change password');
    }
  };

  const handleForgotPasswordRequest = async () => {
    if (!resetEmail) {
      toast.error('Please enter your email');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });

      if (!res.ok) {
        toast.error('Failed to send reset code');
        return;
      }

      toast.success('Reset code sent to your email!');
      setResetStep(2);
    } catch (error) {
      toast.error('Failed to send reset code');
    }
  };

  const handlePasswordReset = async () => {
    if (!resetCode) {
      toast.error('Please enter the reset code');
      return;
    }

    if (!resetNewPassword || resetNewPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }

    if (resetNewPassword !== resetConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: resetEmail,
          reset_code: resetCode,
          new_password: resetNewPassword
        })
      });

      if (!res.ok) {
        const data = await res.json();
        toast.error(data.detail || 'Failed to reset password');
        return;
      }

      toast.success('Password reset successfully! You can now log in.');
      setShowForgotPasswordDialog(false);
      setResetStep(1);
      setResetEmail('');
      setResetCode('');
      setResetNewPassword('');
      setResetConfirmPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-950 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        {/* Login Form */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10" data-testid="login-form">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src="/Primary-Full-Colour-RGB.svg" 
              alt="Reuseabox" 
              className="h-12 sm:h-14 w-auto"
            />
          </div>

          {/* Title */}
          <h2 className="text-xl sm:text-2xl font-normal text-gray-500 text-center mb-10">
            Sign in to your account
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                Email Address
              </label>
              <Input
                data-testid="login-email-input"
                type="email"
                placeholder="your.email@reuseabox.co.uk"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-14 border-2 border-gray-200 focus:border-teal-500 rounded-xl text-base placeholder:text-gray-400"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-bold text-gray-900">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPasswordDialog(true)}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                >
                  Forgot Password?
                </button>
              </div>
              <Input
                data-testid="login-password-input"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-14 border-2 border-gray-200 focus:border-teal-500 rounded-xl text-base placeholder:text-gray-400"
              />
            </div>

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white text-base font-semibold shadow-lg rounded-xl mt-8 transition-all duration-200"
            >
              <span className="mr-2">→</span>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-400 font-medium">Reuseabox HR</p>
          </div>
        </div>
      </div>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordChangeDialog} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Set Your Password</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                For security, please set a new password before continuing.
              </p>
            </div>

            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
                className="mt-2"
              />
            </div>

            <div>
              <Label>Confirm Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                className="mt-2"
              />
            </div>

            <Button
              onClick={handlePasswordChange}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={!newPassword || !confirmPassword}
            >
              Set Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPasswordDialog} onOpenChange={setShowForgotPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>

          {resetStep === 1 ? (
            <div className="space-y-4 py-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-900">
                  <strong>Note:</strong> Email delivery is not yet configured. The reset code will be printed in the backend logs. Please contact your administrator to retrieve the code.
                </p>
              </div>

              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="your.email@reuseabox.co.uk"
                  className="mt-2"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowForgotPasswordDialog(false);
                    setResetEmail('');
                    setResetStep(1);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleForgotPasswordRequest}
                  disabled={!resetEmail}
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                >
                  Send Code
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  A reset code has been sent to <strong>{resetEmail}</strong>
                </p>
              </div>

              <div>
                <Label>Reset Code</Label>
                <Input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  className="mt-2"
                  maxLength={6}
                />
              </div>

              <div>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  placeholder="Enter your new password"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  className="mt-2"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setResetStep(1);
                    setResetCode('');
                    setResetNewPassword('');
                    setResetConfirmPassword('');
                  }}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handlePasswordReset}
                  disabled={!resetCode || !resetNewPassword || !resetConfirmPassword}
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                >
                  Reset Password
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
